import env from "$root/env";

/**
 * Where this deployment's Sphere AI lives, and whether it has one at all.
 *
 * `env.sphereAI` is PARTIAL (see declarations/window.d.ts). The `model` is a product choice and
 * lives in `default.json`, so every environment inherits it; the ADDRESSES — the chat transport,
 * the MCP endpoint, uploads and the conversation store are all one server — are published per
 * environment by the infrastructure that owns that server, and only for an environment whose
 * sphere-mcp actually exists.
 *
 * So it is the addresses, not the block, that say whether the assistant is deployed here. The
 * alternative was a URL in every `runtime-envs/*.json`, which meant every environment shipping
 * coordinates that were right for exactly one of them: production pointed at the dev cluster, and
 * nothing in the type system had an opinion about it. Nobody writes those by hand now, so the
 * wrong value cannot be shipped by inertia.
 *
 * NOTE for local dev: `src/env.ts` resolves `window.env ?? default.json` and `npm run dev`
 * injects nothing, so a local run has the model and no addresses — the surface stays hidden. Add
 * `apiUrl` and `mcpUrl` (pointing at whichever sphere-mcp you want) to
 * `runtime-envs/override.local.json`, which every `npm run dev` layers in, to work on it — per the
 * workspace CLAUDE.md.
 */
export type SphereAIConfig = {
	apiUrl: string;
	mcpUrl: string;
	model: string;
};

/**
 * Whether to mount the Sphere AI surface at all: the feature flag AND an actual deployment to talk
 * to. Two switches because they answer different questions — the flag is "should this environment
 * show it", the addresses are "is there a server there" — and mounting on the flag alone is what
 * would put a panel in front of a user with nowhere to send their question.
 *
 * It tests the ADDRESSES rather than the presence of the block, because `default.json` gives every
 * environment a `sphereAI` carrying the model: a non-null block proves a product default, not a
 * deployment.
 */
export function sphereAIAvailable(): boolean {
	const config = env.sphereAI;
	return env.featureFlags.sphereAI.enabled && !!config?.apiUrl && !!config.mcpUrl;
}

/**
 * This deployment's Sphere AI coordinates.
 *
 * Throws when there are none. Every caller sits under a mount already gated on
 * {@link sphereAIAvailable}, so this is unreachable in a correct tree — and loud rather than
 * `undefined`-shaped if that gate is ever bypassed, because the quiet version of this failure is a
 * request to the string "undefined/conversations".
 */
export function sphereAIConfig(): SphereAIConfig {
	const config = env.sphereAI;
	if (!config?.apiUrl || !config.mcpUrl) {
		throw new Error("Sphere AI is not deployed in this environment (runtime env has no sphere-mcp addresses).");
	}
	return { apiUrl: config.apiUrl, mcpUrl: config.mcpUrl, model: config.model };
}
